import { format, formatDate, parseISO } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { useWorkItemsAPI } from "src/API/central.workItem";
import { useCentralAPI } from "src/API/centralAPI";
import { TypographyStandardTitleH2 } from "src/components/typography"
import { Iteration, Team } from "src/types"





type props = {
  projectName: string,
  TotalWorkItem: number,
  completedWorkItem: number,
  totalTeam: (Team & {
    userCapacity: number, iterations?: {
      userCapacity: number;
      iteration: Iteration;
      workItemCompleted: any[];
      workItemUnCompleted: any[];
      totalWorkItemCount: number;
      totalWorkItemsDetails: WorkItemType[];
    }[]
  })[],
  totalTodayCapacity: number;
  totalPendingCapacityToday: number;
}
type AssignedTo = {
  displayName: string;
  url: string;
  _links: {
    avatar: {
      href: string;
    };
  };
  id: string;
  uniqueName: string;
  imageUrl: string;
  descriptor: string;
};

type WorkItemFields = {
  "System.Id"?: number;
  "System.WorkItemType": string;
  "System.State": string;
  "System.AssignedTo"?: AssignedTo;
  "System.CreatedBy": AssignedTo;
  "System.Title": string;
  "Microsoft.VSTS.Scheduling.RemainingWork"?: number;
  "Custom.Starttime"?: string;
  "Custom.Enddate"?: string;
};

type WorkItemType = {
  id: number;
  rev: number;
  fields: WorkItemFields;
  url: string;
  parentId: number | null;
  startDate?: Date ;
  endDate?: Date;
  organization: string
};


type NestedWorkItemType = WorkItemType & {
  children: NestedWorkItemType[];
};


// Function to group work items by parentId
const groupByParent = (items: WorkItemType[]): { [key: string]: WorkItemType[] } => {
  const grouped: { [key: string]: WorkItemType[] } = {};

  items.forEach(item => {
    const parentId = item.parentId === null || item.parentId === undefined ? 'unparented' : item.parentId.toString(); // Use 'unparented' for tasks with null parentId
    if (!grouped[parentId]) {
      grouped[parentId] = [];
    }
    grouped[parentId].push(item);
  });

  return grouped;
};

// Recursive function to build the nested hierarchy
const buildHierarchy = (grouped: { [key: string]: WorkItemType[] }, parentId: string): NestedWorkItemType[] => {
  const children = grouped[parentId] || [];
  return children.map(item => {
    const nestedItem: NestedWorkItemType = { ...item, children: buildHierarchy(grouped, item.id.toString()) };
    return nestedItem;
  });
};

// Function to nest work items based on parentId
export const nestWorkItems = (workItems: WorkItemType[]): NestedWorkItemType[] => {
  // Group the work items by their parentId
  const groupedItems = groupByParent(workItems);

  // Build the hierarchy starting from 'unparented'
  return buildHierarchy(groupedItems, 'unparented');
};


interface WorkItemListProps {
  workItems: NestedWorkItemType[];
}

const getTypeClass = (workItemType: string, state: string) => {
  const baseClasses = 'p-1 mt-1 rounded-md shadow-sm';
  const opacityClass = state === 'Done' ? 'opacity-50' : '';  // Apply opacity if state is 'Done'

  if (workItemType === 'Task') {
    return `${baseClasses} bg-orange-100 border-l-4 border-orange-500 ${opacityClass}`;
  } else if (workItemType === 'Issue') {
    return `${baseClasses} bg-green-100 border-l-4 border-green-500 ${opacityClass}`;
  }

  return `${baseClasses} ${opacityClass}`;
};

// Define the structure of the identity object
interface Identity {
  displayName: string;
  url: string;
  _links: {
    avatar: {
      href: string;
    };
  };
  id: string;
  uniqueName: string;
  imageUrl: string;
  descriptor: string;
}
interface WorkItemFieldsOld {
  'System.WorkItemType': string;
  'System.State': 'To Do' | 'Doing' | 'On Hold' | 'Done';
  'System.Reason': string;
  'System.AssignedTo'?: Identity;
  'System.CreatedDate': string;
  'System.CreatedBy': Identity;
  'System.ChangedDate': string;
  'System.ChangedBy': Identity;
  'System.CommentCount': number;
  'System.TeamProject': string;
  'System.AreaPath': string;
  'System.IterationPath': string;
  'System.Title': string;
  'System.BoardColumn'?: string;
  'System.BoardColumnDone'?: boolean;
  'Microsoft.VSTS.Common.StateChangeDate': string;
  'Microsoft.VSTS.Common.Priority': number;
  'Microsoft.VSTS.Common.StackRank'?: number;
  'WEF_83D4BB965F9F4862A673CB4FCC34719B_Kanban.Column'?: string;
  'WEF_83D4BB965F9F4862A673CB4FCC34719B_Kanban.Column.Done'?: boolean;
}

// Define the structure of a revision
interface WorkItemRevision {
  id: number;
  rev: number;
  fields: WorkItemFieldsOld;
  url: string;
}

// Helper function to calculate working hours between two dates
function calculateBusinessHours(start: Date, end: Date): number {
  const WORK_START_HOUR = 2.5; // 02:30 AM UTC
  const WORK_END_HOUR = 12.5;  // 12:30 PM UTC
  const MS_PER_HOUR = 1000 * 60 * 60;
  let totalHours = 0;

  let current = new Date(start);
  const endTime = new Date(end);

  while (current < endTime) {
    const dayOfWeek = current.getUTCDay(); // 0 = Sunday, 6 = Saturday
    if (dayOfWeek !== 0) { // Skip Sundays
      const currentUTCHours = current.getUTCHours() + current.getUTCMinutes() / 60;

      // Define the start and end of the workday for the current date
      const dayStart = new Date(current);
      dayStart.setUTCHours(2, 30, 0, 0); // 02:30 AM UTC
      const dayEnd = new Date(current);
      dayEnd.setUTCHours(12, 30, 0, 0); // 12:30 PM UTC

      // Adjust start and end times within working hours
      const periodStart = current > dayStart ? current : dayStart;
      const periodEnd = endTime < dayEnd ? endTime : dayEnd;

      if (periodStart < periodEnd && periodStart >= dayStart && periodEnd <= dayEnd) {
        // Calculate time difference in milliseconds
        const diffMs = periodEnd.getTime() - periodStart.getTime();
        // Convert to hours and minutes
        const totalMinutes = diffMs / (1000 * 60);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = Math.floor(totalMinutes % 60);
        // Treat minutes as decimal (e.g., 39 minutes as 0.39 hours)
        const minuteFraction = minutes / 100;
        totalHours += hours + minuteFraction;
      }
    }

    // Move to the next day
    current = new Date(current);
    current.setUTCDate(current.getUTCDate() + 1);
    current.setUTCHours(0, 0, 0, 0); // Reset to midnight UTC
  }

  return totalHours;
}

export function calculateWorkingHours(revisions: WorkItemRevision[],startDate?: Date, endDate?: Date): number {
  // Sort revisions by ChangedDate to ensure chronological order
  revisions.sort((a, b) =>
    new Date(a.fields['System.ChangedDate']).getTime() -
    new Date(b.fields['System.ChangedDate']).getTime()
  );
  let filteredRevisions = [...revisions];

  // Handle startDate filtering
  if (startDate) {
    const startOfDay = new Date(startDate);

    // Find the latest revision before startDate
    const revisionsBeforeStart = filteredRevisions.filter(
      rev => new Date(rev.fields['System.ChangedDate']).getTime() < startOfDay.getTime()
    );

    // Remove revisions before startDate
    filteredRevisions = filteredRevisions.filter(
      rev => new Date(rev.fields['System.ChangedDate']).getTime() >= startOfDay.getTime()
    );

    // If there were revisions before startDate, create a new revision with startDate
    if (revisionsBeforeStart.length > 0) {
      const latestBeforeStart = revisionsBeforeStart[revisionsBeforeStart.length - 1];
      const newRevision: WorkItemRevision = {
        ...latestBeforeStart,
        fields: {
          ...latestBeforeStart.fields,
          'System.ChangedDate': startOfDay.toISOString()
        }
      };
      filteredRevisions.unshift(newRevision);
    }
  }

  // Handle endDate filtering
  if (endDate) {
    const endOfDay = new Date(endDate);

    // Find the earliest revision after endDate
    const revisionsAfterEnd = filteredRevisions.filter(
      rev => new Date(rev.fields['System.ChangedDate']).getTime() > endOfDay.getTime()
    );

    // Remove revisions after endDate
    filteredRevisions = filteredRevisions.filter(
      rev => new Date(rev.fields['System.ChangedDate']).getTime() <= endOfDay.getTime()
    );

    // If there were revisions after endDate, create a new revision with endDate
    if (revisionsAfterEnd.length > 0) {
      const earliestAfterEnd = revisionsAfterEnd[0];
      const newRevision: WorkItemRevision = {
        ...earliestAfterEnd,
        fields: {
          ...earliestAfterEnd.fields,
          'System.ChangedDate': endOfDay.toISOString()
        }
      };
      filteredRevisions.push(newRevision);
    }else{
      const newRevision: WorkItemRevision = {
        fields:{
         'System.State':'Current',
          'System.ChangedDate': endOfDay.toISOString()
        } 
      } as any;
      filteredRevisions.push(newRevision);
    }
  }

  let totalWorkingHours = 0;
  let stateChanges: { state: string; time: Date }[] = [];
  

  // Extract all state changes with their timestamps
  for (const rev of filteredRevisions) {
    stateChanges.push({
      state: rev.fields['System.State'],
      time: new Date(rev.fields['System.ChangedDate'])
    });
  }

  // If the last state is "Doing", add current time as the endpoint
  const currentDateTime = new Date();
  if (stateChanges.length > 0 && stateChanges[stateChanges.length - 1].state === 'Doing'&&!endDate) {
    stateChanges.push({
      state: 'Current', // Dummy state for calculation
      time: currentDateTime
    });
  }


  // Calculate working hours only for "Doing" periods ending with Done, On Hold, or Current
  for (let i = 0; i < stateChanges.length - 1; i++) {
    if (stateChanges[i].state === 'Doing') {
      const nextState = stateChanges[i + 1].state;
      // Only count hours if next state is Done, On Hold, or Current
      if (nextState === 'Done' || nextState === 'On Hold' || nextState === 'Current' || nextState === 'OnHold') {
        const startTime = stateChanges[i].time;
        const endTime = stateChanges[i + 1].time;

        // Calculate business hours difference
        const hoursDiff = calculateBusinessHours(startTime, endTime);
        totalWorkingHours += hoursDiff;
      }
    }
  }

  return Number(totalWorkingHours.toFixed(2)); // Round to 2 decimal places
}

//outer div

const WorkItemOuterDiv = ({
  item,
  children,
  onDateLoaded
}: {
  item: NestedWorkItemType,
  children: React.ReactNode,
  onDateLoaded?: (startDate: Date | null, endDate: Date | null, hours: number) => void
}) => {
  const divRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const {
    centralData
  } = useCentralAPI({
    trigger: false
  })

  // const {
  //   workItem,
  //   isFetching,
  //   isFetched,
  // } = useWorkItemsAPI({
  //   id: item.id,
  //   organization: item.organization,
  //   trigger: isVisible
  // })

  const WorkItemRevision = centralData?.workItemsWithOrganizationId?.find((workItem) => workItem.id === item.id && workItem.organizationId === item.organization)?.revisions;


  // useEffect(() => {
  //   const observerOptions: IntersectionObserverInit = {
  //     root: null, // Use viewport as the root
  //     rootMargin: '0px', // No margin
  //     threshold: 0.1, // Trigger when 10% of the element is visible
  //   };

  //   const handleIntersection: IntersectionObserverCallback = (entries) => {
  //     entries.forEach((entry) => {
  //       if (entry.isIntersecting) {
  //         // Element is now visible
  //         setIsVisible(true);
  //         observer.unobserve(entry.target); // Stop observing after first visibility (optional)
  //       }
  //     });
  //   };

  //   const observer = new IntersectionObserver(handleIntersection, observerOptions);
  //   if (divRef.current) {
  //     observer.observe(divRef.current);
  //   }

  //   // Cleanup observer on unmount or when item changes
  //   return () => {
  //     if (divRef.current) {
  //       observer.unobserve(divRef.current);
  //     }
  //   };
  // }, [item]); // Dependency on item to re-run if it changes

  useEffect(() => {
    //   if (isFetched) {
    // calculating start date time and end date time
    let startDateIndex = WorkItemRevision?.findIndex(item => item.fields["System.State"] === 'Doing')
    let endDateIndex = WorkItemRevision?.findIndex(item => item.fields["System.State"] === 'Done') || -1
    if(WorkItemRevision&& endDateIndex != WorkItemRevision?.length - 1){
        endDateIndex = -1
    }

    const hours = calculateWorkingHours(WorkItemRevision || [],item.startDate, item.endDate)
    startDateIndex == undefined ? startDateIndex = -1 : startDateIndex = startDateIndex
    endDateIndex == undefined ? endDateIndex = -1 : endDateIndex = endDateIndex

    const startDate = startDateIndex >= 0 && WorkItemRevision?.[startDateIndex]?.fields?.["System.ChangedDate"] ? new Date(WorkItemRevision?.[startDateIndex]?.fields?.["System.ChangedDate"]) : null
    const endDate = endDateIndex >= 0 && WorkItemRevision?.[endDateIndex]?.fields?.["System.ChangedDate"] ? new Date(WorkItemRevision?.[endDateIndex]?.fields?.["System.ChangedDate"]) : null
    onDateLoaded?.(startDate, endDate, hours)
    //   }
  }, [])

  return (
    <div
      key={item.id}
      ref={divRef}
      data-work-item-id={item.id} // Add data attribute to identify the work item
      className={getTypeClass(item.fields['System.WorkItemType'], item.fields['System.State'])}>
      {children}
    </div>
  )
}

// Function to render work items recursively
function RenderWorkItems({ items }: { items: NestedWorkItemType[] }) {

  const [dates, setDates] = useState<Record<string, { start: Date | null; end: Date | null, hours: number }>>({});

  const setDatesForItem = (id: number, startDate: Date | null, endDate: Date | null, hours: number) => {
    setDates((prev) => ({
      ...prev,
      [id.toString()]: { start: startDate, end: endDate, hours: hours }
    }));
  };

  return (<>{items.map((item) => (
    <WorkItemOuterDiv
      item={item}
      key={item.id}
      onDateLoaded={(start, end, hour) => setDatesForItem(item.id, start, end, hour)}
    >
      <h3 className="text-xs font-semibold">{item.fields['System.Title']}</h3>
      {/* <p className="text-sm italic">{item.fields['System.WorkItemType']}</p> */}
      <p className="text-xs text-gray-500">State: {item.fields['System.State']}</p> {/* Display State */}
      {item.fields['System.WorkItemType'] === 'Issue' && <p className="text-xs text-gray-500 whitespace-nowrap">Start Date: {dates[item.id]?.start ? formatDate(new Date(dates[item.id]?.start!), "dd-MMM-yyyy") : ""}</p>}
      {item.fields['System.WorkItemType'] === 'Issue' && <p className="text-xs text-gray-500">End date: {dates[item.id]?.end ? formatDate(new Date(dates[item.id]?.end!), "dd-MMM-yyyy") : ""}</p>}
      {<div className=" text-xs text-gray-500">hours: {dates[item.id]?.hours}</div>}
      {item.children.length > 0 && (
        <div className="ml-4 mt-2 "><RenderWorkItems items={item.children} /></div>
      )}
    </WorkItemOuterDiv>
  ))}</>);
};

export const WorkItemList: React.FC<WorkItemListProps> = ({ workItems }) => {
  // Function to apply styles based on WorkItemType


  return <div className="space-y-3"><RenderWorkItems items={workItems} /></div>;
};




export function ProjectComponent(props: props) {



  return (
    <div className='border bg-gray-200 p-2 rounded-md border-gray-700  w-full flex flex-col'>
      <div className='w-full flex gap-2 md:col-span-3 '>
        <TypographyStandardTitleH2>{props.projectName}</TypographyStandardTitleH2>
      </div>
      <div className=' flex col-span-1 md:col-span-2'>
        <div className=' flex flex-col w-full '>

          <div className='flex'>
            <TypographyStandardTitleH2>Total Work Item: </TypographyStandardTitleH2>
            <p className=" pl-1">{props.TotalWorkItem}</p>
          </div>
          <div className='flex'>
            <TypographyStandardTitleH2>Completed Work Item: </TypographyStandardTitleH2>
            <p className="pl-1">{props.completedWorkItem}</p>
          </div>
          {
            props.totalTeam.map((team) => {

              return (
                <div key={team.id} className="border bg-gray-700 p-2 rounded-md flex flex-col gap-3">
                  <div className='flex text-white'>
                    <TypographyStandardTitleH2 className=" text-white" >Team:  </TypographyStandardTitleH2>
                    <p className="pl-1">{team.name}</p>
                  </div>
                  {team.iterations?.map((iteration) => {
                    // Parse the ISO string into a JavaScript Date object
                    const startDate = iteration.iteration.attributes.startDate ? parseISO(iteration.iteration.attributes.startDate) : ''
                    // Format the date to 'dd-MM-yyyy'
                    const formattedStartDate = startDate ? format(startDate, 'dd-MM-yyyy') : '';
                    // Parse the ISO string into a JavaScript Date object
                    const finishDate = iteration.iteration.attributes.finishDate ? parseISO(iteration.iteration.attributes.finishDate) : ''
                    // Format the date to 'dd-MM-yyyy'
                    const formattedFinishDate = finishDate ? format(finishDate, 'dd-MM-yyyy') : '';

                    //check the end date exeeded current date.
                    const currentDate = new Date();
                    const endDate = new Date(iteration.iteration.attributes.finishDate);
                    endDate.setHours(23, 59, 59, 999);
                    currentDate.setHours(0, 0, 0, 0);
                    const isEndDateExeeded = endDate < currentDate

                    //make the work items nested
                    const nestedWorkItems = nestWorkItems(iteration.totalWorkItemsDetails);



                    return (
                      <div className={`border p-2 rounded-md 
                                            ${iteration.iteration.attributes.timeFrame === 'current' && !isEndDateExeeded ? 'bg-blue-200' : `
                                                ${iteration.iteration.attributes.timeFrame === 'past' || isEndDateExeeded ?
                            iteration.workItemUnCompleted.length > 0 ? 'bg-red-200' : 'bg-green-200'
                            : ''}
                                                `}
                                            `}>
                        <div className='flex'>
                          <TypographyStandardTitleH2>IterationName: </TypographyStandardTitleH2>
                          <p className="pl-1">{iteration.iteration.name}</p>
                        </div>
                        <div className='flex'>
                          <TypographyStandardTitleH2>Start Date:  </TypographyStandardTitleH2>
                          <p className="pl-1">{formattedStartDate}</p>
                        </div>
                        <div className='flex'>
                          <TypographyStandardTitleH2>End Date:  </TypographyStandardTitleH2>
                          <p className="pl-1">{formattedFinishDate}</p>
                        </div>
                        <div className='flex'>
                          <TypographyStandardTitleH2>Work Items status:  </TypographyStandardTitleH2>
                          <p className="pl-1">{iteration.workItemCompleted.length}/{iteration.totalWorkItemCount}</p>
                        </div>

                        <div className='flex'>
                          <TypographyStandardTitleH2>Capacity today: </TypographyStandardTitleH2>
                          <p className="pl-1">{iteration.userCapacity}</p>
                        </div>
                        <div className=" bg-white p-2 rounded-md">
                          {
                            <WorkItemList workItems={nestedWorkItems} />
                          }
                        </div>
                      </div>
                    )
                  })}
                </div>
              )
            })
          }
          <div className='flex'>
            <TypographyStandardTitleH2>Total today Capacity: </TypographyStandardTitleH2>
            <p className="pl-1">{props.totalTodayCapacity}</p>
          </div>
          <div className='flex'>
            <TypographyStandardTitleH2>Today pending Capacity: </TypographyStandardTitleH2>
            <p className="pl-1">{props.totalPendingCapacityToday}</p>
          </div>
        </div>
      </div>
    </div>
  )

}